import { safeParseFloat } from '@/lib/utils'

export type PriceAnchor = 'price' | 'mid' | 'best_bid' | 'best_ask'
export type OffsetUnit = 'quote_value' | 'bps' | 'percent' | 'ticks'

export interface AnchorPrice {
  bestBidPrice?: string
  bestAskPrice?: string
}

/** Shared by limit-price-field.tsx (UI hint) and build-order-preview-request.ts
 * (preview payload) so the two can't compute a different "mid price" for the
 * same anchor. */
export function computeAnchorPrice(anchor: PriceAnchor, ticker?: AnchorPrice): number | null {
  if (!ticker) return null
  const bid = safeParseFloat(ticker.bestBidPrice ?? '') || 0
  const ask = safeParseFloat(ticker.bestAskPrice ?? '') || 0
  if (anchor === 'mid') return bid > 0 && ask > 0 ? (bid + ask) / 2 : null
  if (anchor === 'best_bid') return bid > 0 ? bid : null
  if (anchor === 'best_ask') return ask > 0 ? ask : null
  return null
}

/** Resolves a relative anchor + offset (as entered in the "Mid price / 0 /
 * BPS" field) into an absolute price, the same math the UI's "Estimated
 * price" hint already shows the user. Returns null when the anchor price
 * can't be resolved (no live ticker yet) rather than falling back to 0/NaN. */
export function computeEstimatedPrice(
  anchor: PriceAnchor,
  offset: number,
  offsetUnit: OffsetUnit,
  ticker?: AnchorPrice,
  tickSize?: number
): number | null {
  const anchorPrice = computeAnchorPrice(anchor, ticker)
  if (anchorPrice === null || anchorPrice <= 0) return null

  let finalPrice: number
  if (offsetUnit === 'quote_value') finalPrice = anchorPrice + offset
  else if (offsetUnit === 'percent') finalPrice = anchorPrice * (1 + offset / 100)
  else if (offsetUnit === 'bps') finalPrice = anchorPrice * (1 + offset / 10000)
  else finalPrice = anchorPrice + offset * (tickSize ?? 1)

  if (finalPrice <= 0) return null
  if (tickSize && tickSize > 0) finalPrice = Math.round(finalPrice / tickSize) * tickSize
  return finalPrice
}
